import { useCallback, useState } from "react"

/**
 * Page-owned open/closed state for a dashboard section, persisted to
 * localStorage. The page needs to own this (rather than letting a collapsible
 * component manage it internally) so it can gate the section's data query with
 * `enabled: isOpen` — a collapsed section then fires no network request until
 * the user expands it.
 */
export function useCollapsibleSection(storageKey: string, defaultExpanded: boolean) {
  const [isOpen, setIsOpenState] = useState<boolean>(() => {
    if (typeof window === "undefined") return defaultExpanded
    try {
      const stored = localStorage.getItem(storageKey)
      return stored !== null ? stored === "true" : defaultExpanded
    } catch {
      // localStorage can throw on read in restricted-storage environments
      // (e.g. Safari private browsing) — fall back to the default.
      return defaultExpanded
    }
  })

  const setIsOpen = useCallback(
    (open: boolean) => {
      setIsOpenState(open)
      try {
        localStorage.setItem(storageKey, String(open))
      } catch {
        // localStorage unavailable (private mode / quota) — open state simply
        // won't persist across reloads, which is acceptable.
      }
    },
    [storageKey]
  )

  return { isOpen, setIsOpen }
}
